import {monthLabel} from '@vat/engine/month';
import {profileAt} from '@vat/engine/tax-profile';
import {VAT_EXPORT_COPY, VAT_FORM_LABELS, VAT_OBJEKT_COPY, VAT_REVIEW_W2_COPY, VAT_TAX_STATUS_LABELS} from '@vat/vat-copy';
import {formatDateDE, formatEuro, formatPercent, steuernummerLabel} from './vat-format';
import type {ObjektAbrechnungInput, ObjektAbrechnungModel, ObjektAreaMonth, ObjektKontoRow, ObjektRateGroup, ObjektUnitRow} from './vat-objekt-abrechnung';

/**
 * PDF der „Umsatzsteuerabrechnung je Objekt“ (Spec 2026-09-13 Sektion 2.3, Task C1). A4 hoch, Kopf wie Kz-48-Nachweis,
 * ohne Briefpapier. Seite 1: Kopf, Erträge/Kosten je Steuersatz (0/7/19) mit Summen, „Nicht steuerbar“, Ergebnisbox.
 * Seite 2: Flächenverlauf und Einheiten zum letzten Monat. Knoten-IDs `objekt-*` für Tests und Guard.
 *
 * Breiten-Guard: feste Spaltenbreiten + Zellabstand (2 pt links/rechts) lassen der Sternspalte jeder Tabelle ≥ 80 pt
 * (`OBJEKT_PDF_MIN_STAR_WIDTH`), damit auf A4 hoch (595,28 − 2 × 40 pt) nichts rechts abgeschnitten wird.
 *
 * Entscheidungen (Plan lässt offen): Saldo 0 → „ausgeglichen“; Leerzustände als Hinweistext mit derselben Knoten-ID;
 * die Prüfsumme fehlt in der Fußzeile, weil das Modell keinen `inputHash` trägt (Live-Berechnung, Task C2).
 *
 * @author Anthony Nahas
 * @since 13.09.26
 */
export const OBJEKT_PDF_PAGE_MARGIN_X = 40;
export const OBJEKT_PDF_CONTENT_WIDTH = 595.28 - 2 * OBJEKT_PDF_PAGE_MARGIN_X;
export const OBJEKT_PDF_MIN_STAR_WIDTH = 80;

const COLOR_MUTED = '#6b7280';
const COLOR_HEADER_FILL = '#f3f4f6';
const COLOR_RESULT_FILL = '#eef2ff';
const FONT_SMALL = 8;
const FONT_TABLE = 7;

/** Konto · Bezeichnung(*) · Brutto · Netto · USt · anrechenbar · anrechenbare USt – fest 362 pt + 7 × 4 pt Abstand → Stern 125 pt. */
const KONTO_TABLE_WIDTHS: Array<number | '*'> = [46, '*', 62, 62, 56, 70, 66];
/** Monat(*) · Gesamtfläche · steuerpflichtig · Anteil · Modus. */
const AREA_TABLE_WIDTHS: Array<number | '*'> = ['*', 80, 90, 60, 120];
/** Art · Bezeichnung(*) · Nutzer · m² · steuerpflichtig · Leerstand. */
const UNIT_TABLE_WIDTHS: Array<number | '*'> = [70, '*', 130, 44, 66, 56];
/** Bezeichnung/Grund(*) · Brutto. */
const NON_TAXABLE_WIDTHS: Array<number | '*'> = ['*', 80];
const INFO_WIDTHS: Array<number | '*'> = [130, '*'];

/** Wie 'lightHorizontalLines', aber 2 pt Zellabstand – Platz für sieben Spalten auf A4 hoch (Muster Anlage A). */
const COMPACT_LAYOUT = {
    hLineWidth: (i: number, node: any): number => (i === 0 || i === 1 || i === node.table.body.length ? 1 : 0.5),
    vLineWidth: (): number => 0,
    hLineColor: (i: number, node: any): string => (i === 0 || i === 1 || i === node.table.body.length ? '#9ca3af' : '#e5e7eb'),
    paddingLeft: (): number => 2,
    paddingRight: (): number => 2,
    paddingTop: (): number => 2,
    paddingBottom: (): number => 2,
};

const headerCell = (text: string, alignment: 'left' | 'right' = 'left'): any => ({text, bold: true, fontSize: FONT_TABLE, fillColor: COLOR_HEADER_FILL, alignment, margin: [0, 2, 0, 2]});
const cell = (text: string, options: Record<string, unknown> = {}): any => ({text, fontSize: FONT_TABLE, margin: [0, 1, 0, 1], ...options});
const infoCell = (text: string, options: Record<string, unknown> = {}): any => ({text, fontSize: FONT_SMALL, margin: [2, 2, 2, 2], ...options});
const right = (text: string, options: Record<string, unknown> = {}): any => cell(text, {alignment: 'right', ...options});
const sectionTitle = (text: string): any => ({text, bold: true, fontSize: 11, margin: [0, 8, 0, 6]});
const emptyText = (id: string, text: string): any => ({id, text, fontSize: FONT_SMALL, color: COLOR_MUTED, margin: [0, 0, 0, 10]});

const formatArea = (value: number | null): string =>
    value === null || value === undefined || !Number.isFinite(value) ? VAT_OBJEKT_COPY.table.leer : new Intl.NumberFormat('de-DE', {maximumFractionDigits: 2}).format(value);

const unitTypeLabel = (categoryType: string | null): string => (categoryType ? VAT_OBJEKT_COPY.unitTypes[categoryType] ?? categoryType : VAT_OBJEKT_COPY.table.leer);
const areaModeLabel = (mode: string): string => VAT_OBJEKT_COPY.areaModes[mode] ?? mode;
const yesNo = (value: boolean): string => (value ? VAT_OBJEKT_COPY.table.ja : VAT_OBJEKT_COPY.table.nein);

/** Besteuerungsform (Soll/Ist) zum Beginn des Zeitraums – Profil-Zeitleiste wie im Rechenkern. */
export const objektBesteuerungLabel = (input: Pick<ObjektAbrechnungInput, 'taxProfile' | 'period'>): string =>
    VAT_FORM_LABELS[profileAt(input.taxProfile, input.period.start).form];

/** Saldo-Text: positiv = USt-Zahllast, negativ = Überschuss, 0 = ausgeglichen. */
export const objektSaldoKind = (saldo: number): string => {
    const copy = VAT_OBJEKT_COPY.ergebnis;
    if (saldo > 0) {
        return copy.zahllast;
    }
    return saldo < 0 ? copy.ueberschuss : copy.ausgeglichen;
};

export const objektPeriodLabel = (period: ObjektAbrechnungInput['period']): string =>
    period.periodKey ? period.label : VAT_OBJEKT_COPY.header.kalenderjahr(period.year);

export const buildObjektFooterText = (input: Pick<ObjektAbrechnungInput, 'now' | 'engineVersion'>): string =>
    VAT_OBJEKT_COPY.footer({datum: formatDateDE(input.now), engineVersion: input.engineVersion});

const kontoRowCells = (row: ObjektKontoRow): any[] => [
    cell(row.konto),
    cell(row.label),
    right(formatEuro(row.brutto)),
    right(formatEuro(row.netto)),
    right(formatEuro(row.ust)),
    right(row.anrechenbarLabel),
    right(formatEuro(row.anrechenbareUst)),
];

const kontoTable = (id: string, group: ObjektRateGroup): any => {
    const t = VAT_OBJEKT_COPY.table;
    const header = [t.konto, t.bezeichnung, t.brutto, t.netto, t.ust, t.anrechenbar, t.anrechenbareUst].map((h, i) => headerCell(h, i >= 2 ? 'right' : 'left'));
    const lineCount = group.rows.reduce((a, r) => a + r.lineCount, 0);
    const total = [
        cell(t.summe(group.rate), {bold: true}),
        cell(t.zeilen(lineCount), {color: COLOR_MUTED}),
        right(formatEuro(group.sum.brutto), {bold: true}),
        right(formatEuro(group.sum.netto), {bold: true}),
        right(formatEuro(group.sum.ust), {bold: true}),
        cell(''),
        right(formatEuro(group.sum.anrechenbareUst), {bold: true}),
    ];
    return {id, table: {headerRows: 1, widths: KONTO_TABLE_WIDTHS, body: [header, ...group.rows.map(kontoRowCells), total]}, layout: COMPACT_LAYOUT, margin: [0, 0, 0, 10]};
};

const rateSection = (idPrefix: string, title: string, groups: ObjektRateGroup[], empty: string): any[] => {
    const nodes: any[] = [sectionTitle(title)];
    if (!groups.length) {
        nodes.push(emptyText(`${idPrefix}-empty`, empty));
        return nodes;
    }
    groups.forEach(group => {
        nodes.push({text: VAT_OBJEKT_COPY.sections.rateGroup(title, group.rate), fontSize: FONT_SMALL, color: COLOR_MUTED, margin: [0, 0, 0, 3]});
        nodes.push(kontoTable(`${idPrefix}-${group.rate}`, group));
    });
    return nodes;
};

const nonTaxableNode = (rows: ObjektKontoRow[]): any => {
    if (!rows.length) {
        return emptyText('objekt-nicht-steuerbar', VAT_OBJEKT_COPY.empty.nichtSteuerbar);
    }
    const header = [headerCell(VAT_OBJEKT_COPY.table.grund), headerCell(VAT_OBJEKT_COPY.table.brutto, 'right')];
    const body = rows.map(row => [cell(row.label), right(formatEuro(row.brutto))]);
    return {id: 'objekt-nicht-steuerbar', table: {headerRows: 1, widths: NON_TAXABLE_WIDTHS, body: [header, ...body]}, layout: COMPACT_LAYOUT, margin: [0, 0, 0, 10]};
};

/** Ergebnisbox: Saldo-Text links ohne Betrag („Saldo – USt-Zahllast“), Betrag nur rechts (Review-MINORs Wave 2). */
const resultNode = (model: ObjektAbrechnungModel): any => {
    const copy = VAT_OBJEKT_COPY.ergebnis;
    const kind = objektSaldoKind(model.totals.saldo);
    return {
        id: 'objekt-ergebnis',
        table: {
            widths: ['*', 110],
            body: [
                [infoCell(copy.ertraegeUst), infoCell(formatEuro(model.totals.ertraegeUst), {alignment: 'right'})],
                [infoCell(copy.kostenAnrechenbar), infoCell(formatEuro(model.totals.kostenAnrechenbar), {alignment: 'right'})],
                [
                    infoCell(VAT_REVIEW_W2_COPY.objekt.saldoLabel(kind), {bold: true, fontSize: 10, fillColor: COLOR_RESULT_FILL}),
                    infoCell(formatEuro(model.totals.saldo), {bold: true, fontSize: 10, alignment: 'right', fillColor: COLOR_RESULT_FILL}),
                ],
            ],
        },
        layout: 'lightHorizontalLines',
        margin: [0, 4, 0, 0],
    };
};

const areaTable = (months: ObjektAreaMonth[]): any => {
    if (!months.length) {
        return emptyText('objekt-flaechen-table', VAT_OBJEKT_COPY.empty.flaechen);
    }
    const t = VAT_OBJEKT_COPY.table;
    const header = [headerCell(t.monat), headerCell(t.gesamtflaeche, 'right'), headerCell(t.steuerpflichtigeFlaeche, 'right'), headerCell(t.anteil, 'right'), headerCell(t.modus)];
    const body = months.map(m => [cell(monthLabel(m.monthKey)), right(formatArea(m.totalArea)), right(formatArea(m.taxableArea)), right(formatPercent(m.share)), cell(areaModeLabel(m.mode))]);
    return {id: 'objekt-flaechen-table', table: {headerRows: 1, widths: AREA_TABLE_WIDTHS, body: [header, ...body]}, layout: COMPACT_LAYOUT, margin: [0, 0, 0, 10]};
};

const unitTable = (units: ObjektUnitRow[]): any => {
    if (!units.length) {
        return emptyText('objekt-einheiten-table', VAT_OBJEKT_COPY.empty.einheiten);
    }
    const t = VAT_OBJEKT_COPY.table;
    const header = [headerCell(t.art), headerCell(t.bezeichnung), headerCell(t.nutzer), headerCell(t.flaeche, 'right'), headerCell(t.steuerpflichtig), headerCell(t.leerstand)];
    const body = units.map(u => [cell(unitTypeLabel(u.categoryType)), cell(u.label), cell(u.tenantsName ?? t.leer), right(formatArea(u.area)), cell(yesNo(u.taxable)), cell(yesNo(u.vacant))]);
    return {id: 'objekt-einheiten-table', table: {headerRows: 1, widths: UNIT_TABLE_WIDTHS, body: [header, ...body]}, layout: COMPACT_LAYOUT};
};

/** Stichtags-Monat der Einheitenliste: letzter Flächenmonat, sonst Zeitraumende. */
const stichtagMonthLabel = (input: ObjektAbrechnungInput): string => {
    const last = input.areaMonths[input.areaMonths.length - 1];
    if (last) {
        return monthLabel(last.monthKey);
    }
    const end = input.period.end;
    return monthLabel(`${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}`);
};

/** pdfmake-Dokumentdefinition; ohne Hintergrund (kein Briefpapier), Knoten-IDs objekt-*. */
export const buildVatObjektDocDefinition = (model: ObjektAbrechnungModel, input: ObjektAbrechnungInput): any => {
    const copy = VAT_OBJEKT_COPY;
    const footerText = buildObjektFooterText(input);
    const periodText = objektPeriodLabel(input.period);
    const rangeText = copy.header.zeitraumRange(formatDateDE(input.period.start), formatDateDE(input.period.end));

    return {
        info: {title: copy.docTitle(input.property.label, periodText), author: 'Nahaus'},
        pageSize: 'A4',
        pageMargins: [OBJEKT_PDF_PAGE_MARGIN_X, 50, OBJEKT_PDF_PAGE_MARGIN_X, 60],
        content: [
            {
                id: 'objekt-header',
                stack: [
                    {text: copy.title, bold: true, fontSize: 16},
                    {text: copy.subtitle, color: COLOR_MUTED, fontSize: 9},
                    {
                        id: 'objekt-header-table',
                        table: {
                            widths: INFO_WIDTHS,
                            body: [
                                [infoCell(copy.header.objekt, {color: COLOR_MUTED}), infoCell(input.property.label, {bold: true})],
                                [infoCell(copy.header.eigentuemer, {color: COLOR_MUTED}), infoCell([input.landlord.name, input.landlord.addressLine].filter(Boolean).join(', '))],
                                [infoCell(copy.header.einordnung, {color: COLOR_MUTED}), infoCell(VAT_TAX_STATUS_LABELS[input.taxProfile.status])],
                                [infoCell(copy.header.besteuerung, {color: COLOR_MUTED}), infoCell(objektBesteuerungLabel(input))],
                                [infoCell(copy.header.steuernummer, {color: COLOR_MUTED}), infoCell(steuernummerLabel(input.taxProfile))],
                                [infoCell(copy.header.zeitraum, {color: COLOR_MUTED}), infoCell(`${periodText} (${rangeText})`)],
                            ],
                        },
                        layout: 'lightHorizontalLines',
                        margin: [0, 10, 0, 6],
                    },
                ],
            },
            ...rateSection('objekt-ertraege', copy.sections.ertraege, model.ertraege, copy.empty.ertraege),
            ...rateSection('objekt-kosten', copy.sections.kosten, model.kosten, copy.empty.kosten),
            sectionTitle(copy.sections.nichtSteuerbar),
            nonTaxableNode(model.nichtSteuerbar),
            sectionTitle(copy.sections.ergebnis),
            resultNode(model),
            {
                id: 'objekt-flaechen',
                pageBreak: 'before',
                stack: [sectionTitle(copy.sections.flaechen), areaTable(input.areaMonths)],
            },
            {
                id: 'objekt-einheiten',
                stack: [sectionTitle(copy.sections.einheiten(stichtagMonthLabel(input))), unitTable(input.units)],
            },
        ],
        footer: (currentPage: number, pageCount: number): any => ({
            stack: [
                {text: footerText, fontSize: 7, color: COLOR_MUTED, alignment: 'center', margin: [OBJEKT_PDF_PAGE_MARGIN_X, 0, OBJEKT_PDF_PAGE_MARGIN_X, 0]},
                {text: VAT_EXPORT_COPY.pdf.page(currentPage, pageCount), fontSize: 7, color: COLOR_MUTED, alignment: 'center', margin: [0, 2, 0, 0]},
            ],
        }),
        defaultStyle: {fontSize: 9},
    };
};
